import { defineStore } from "pinia";
import { ref, computed } from "vue";
import * as authService from "@/services/auth.js";
import { fetchCurrentUser } from "@/services/api.js";
import type { UserInfo, UserStats } from "@/types/index.js";
import type { User } from "firebase/auth";

export const useAuthStore = defineStore("auth", () => {
    const user = ref<UserInfo | null>(null);
    const userStats = ref<UserStats | null>(null);
    const loading = ref(true);
    const error = ref<string | null>(null);

    // Track initialization state to prevent duplicate listeners
    let initPromise: Promise<void> | null = null;
    let unsubscribeAuth: (() => void) | null = null;

    const isAuthenticated = computed(() => !!user.value);

    const isSuperAdmin = computed(() => user.value?.roles.includes("super_admin") ?? false);

    const canCreatePoll = computed(() => userStats.value?.canCreatePoll ?? false);

    function initialize(): Promise<void> {
        // Return existing promise if already initializing or initialized
        if (initPromise) {
            return initPromise;
        }

        loading.value = true;

        initPromise = new Promise((resolve) => {
            unsubscribeAuth = authService.onAuthChange(async (firebaseUser: User | null) => {
                if (firebaseUser) {
                    const tokenResult = await authService.getIdTokenResult();

                    user.value = {
                        uid: firebaseUser.uid,
                        email: firebaseUser.email,
                        roles: (tokenResult?.claims.roles as string[]) || [],
                    };

                    try {
                        userStats.value = await fetchCurrentUser();
                    } catch {
                        // Non-critical, user can still use the app
                    }
                } else {
                    user.value = null;
                    userStats.value = null;
                }

                loading.value = false;
                resolve();
            });
        });

        return initPromise;
    }

    function dispose(): void {
        if (unsubscribeAuth) {
            unsubscribeAuth();
            unsubscribeAuth = null;
        }
        initPromise = null;
    }

    async function login(email: string, password: string): Promise<void> {
        error.value = null;

        try {
            await authService.signIn(email, password);
        } catch (e) {
            error.value = e instanceof Error ? e.message : "Login failed";
            throw e;
        }
    }

    async function logout(): Promise<void> {
        error.value = null;

        try {
            await authService.signOut();
            user.value = null;
            userStats.value = null;
        } catch (e) {
            error.value = e instanceof Error ? e.message : "Logout failed";
            throw e;
        }
    }

    async function refreshStats(): Promise<void> {
        if (!user.value) return;

        try {
            userStats.value = await fetchCurrentUser();
        } catch {
            // Ignore errors
        }
    }

    function clearError(): void {
        error.value = null;
    }

    return {
        user,
        userStats,
        loading,
        error,
        isAuthenticated,
        isSuperAdmin,
        canCreatePoll,
        initialize,
        dispose,
        login,
        logout,
        refreshStats,
        clearError,
    };
});
